import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  Paper,
  IconButton,
  Skeleton,
  Link,
  TextField,
  Button,
  Snackbar,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQueryClient } from "react-query";
import {
  MonetizationOn as PayoutIcon,
  Campaign as CampaignIcon,
  Handshake as PartnershipIcon,
  Send as SendIcon,
  PersonAdd as JoinIcon,
  Telegram as PostIcon,
  People as UserIcon,
  Payments as PaymentIcon,
} from "@mui/icons-material";
import API from "../../../../API";
import { useCreatorAuth } from "../../../../Hooks/creator-use-auth";
import useAuth from "../../../../Hooks/use-auth"; // 👈 NEW

/* ──────────────────────────────────────────────────────────────────
 *  Config
 * ─────────────────────────────────────────────────────────────────*/
const ROW_COUNT = 20;

/* random Unsplash fall‑backs */
const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2",
  "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330",
  "https://images.unsplash.com/photo-1517841905240-472988babdf9",
  "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e",

  // new gradient / abstract splash images:
  "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1511765224389-37f0e77cf0eb?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1523978591478-c753949ff840?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1499084732479-de2c02d45fc4?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1581276879432-cce6676229c8?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1522312346375-d1a52e2b99b3?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1606788075767-0569f5d3d686?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1508371916422-f50bbd4b75a6?auto=format&fit=crop&w=800&q=80"
];


/* icon map */
const activityIcons = {
  Payout: <PayoutIcon fontSize="small" />,
  Campaign: <CampaignIcon fontSize="small" />,
  Partnership: <PartnershipIcon fontSize="small" />,
  Pitch: <SendIcon fontSize="small" />,
  Join: <JoinIcon fontSize="small" />,
  Post: <PostIcon fontSize="small" />,
  User: <UserIcon fontSize="small" />,
  Payment: <PaymentIcon fontSize="small" />, // ⟵ NEW
};

/* ──────────────────────────────────────────────────────────────────
 *  Activity summary guts – SAME for all rows now, with universal link when
 *  creator_id is present (covers Posts & other types)
 * ─────────────────────────────────────────────────────────────────*/
const ActivitySummary = ({ activity }) => {
  const type = activity.activity_type?.trim();
  const {
    activity_date,
    activity_name,
    creator_id,
    display_name = creator_id,
  } = activity;

  const formattedDate = new Date(activity_date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  /* Build description sentence */
  let description = "";
  switch (type) {
    case "Post":
      description = activity_name;
      break;
    case "Campaign":
      description = `started ${activity_name}`;
      break;
    case "Pitch":
      description = `pitched for ${activity_name}`;
      break;
    case "Payout":
      description = "received a payout";
      break;
    case "Partnership":
      description = "started a partnership";
      break;
    case "Join":
      description = "joined Blitz";
      break;
    case "User":
      description = "just joined Blitz";
      break;
    case "Payment":
      description = "sent a payment";
      break;
    default:
      description = activity_name;
  }

  return (
    <Box>
      <Typography fontWeight={600}>{display_name || "A New User"}</Typography>

      {/* description row – universally linked when creator_id exists */}
      <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
        {creator_id ? (
          <>
            <Link
              href={`https://www.useblitz.co/creators/${creator_id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {creator_id}
            </Link>{" "}
            {description}
          </>
        ) : (
          description
        )}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={0.5}>
        {formattedDate}
      </Typography>
    </Box>
  );
};

/* ──────────────────────────────────────────────────────────────────
 *  Main component
 * ─────────────────────────────────────────────────────────────────*/
export default function ZapFeed() {
  const theme      = useTheme();
   const navigate   = useNavigate();
   const qc         = useQueryClient();

   // 1) Identify current session (creator vs user)
   const { creatorToken }                       = useCreatorAuth();
   const { isAuthorized: userOK, getCurrrentUser } = useAuth();
    const { isAuthorized: creatorOK } = useCreatorAuth();

   // now it's safe to use userOK & creatorToken
   useEffect(() => {
  const late = window._pendingApnsToken;

  // only send when we actually have a token *and* someone is logged in
  if (!late || (!userOK && !creatorOK)) return;

  API.notifications
    .registerDeviceToken({ device_token: late, platform: 'ios' })
    .then(() => { window._pendingApnsToken = null; })
    .catch(console.error);
}, [userOK, creatorOK]);



  const isCreator = !!creatorToken?.creator_user; // 👈 helper flag for feature‑gating

  let displayName = "Me";
  let photo = PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)];

  if (isCreator) {
    const creator = creatorToken.creator_user.creator;
    displayName =
      creator?.display_name || creatorToken.creator_user.username || displayName;
    photo =
      creator?.pfphref ||
      creatorToken.creator_user?.pfphref ||
      API.currentUser?.pfphref ||
      photo;
  } else if (userOK) {
    const user = getCurrrentUser();
    displayName =
      user?.first_name || user?.last_name
        ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
        : user?.email || user?.username || displayName;
    photo = user?.phphref || photo;
  }

  /* ── 2. LOCAL STATE ────────────────────────────────────────*/
  const [text, setText] = useState("");
  const [snack, setSnack] = useState(null); // {msg, sev}

  /***********************  react‑query: feed  ***************************/
  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    refetch,
  } = useInfiniteQuery(
    ["zapfeed4"],
    ({ pageParam = 0 }) => API.zapfeed.list({ page: pageParam, rows: ROW_COUNT }),
    {
      getNextPageParam: (last, pages) =>
        last.zapfeed.length === ROW_COUNT ? pages.length : undefined,
      staleTime: 3_600_000,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
  if (userOK || creatorToken) {
    if (window._pendingApnsToken) {
      API.notifications
        .registerDeviceToken({ device_token: window._pendingApnsToken, platform: 'ios' })
        .then(() => (window._pendingApnsToken = null))
        .catch(console.error);
    }
    refetch();
  }
}, [userOK, creatorToken, refetch]);


  /***********************  infinite‑scroll sentinel  *********************/
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) fetchNextPage();
      },
      { rootMargin: "400px 0px 0px 0px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [fetchNextPage, hasNextPage]);

  /***********************  mutation: create post  ************************/
  const createPost = useMutation(API.zapfeed.post, {
    onSuccess: (resp) => {
      const p = resp.post;
      const newItem = {
        activity_id: p.id,
        activity_name: p.text,
        activity_type: "Post",
        activity_date: p.created_at,
        creator_id: null,
        amount: null,
        pfphref: photo,
        display_name: displayName,
      };

      qc.setQueryData(["zapfeed4"], (old) => {
        if (!old) return old;
        const first = old.pages[0];
        const updatedFirst = { ...first, zapfeed: [newItem, ...first.zapfeed] };
        return { ...old, pages: [updatedFirst, ...old.pages.slice(1)] };
      });
      setText("");
      setSnack({ msg: "Posted!", sev: "success" });
    },
    onError: (err) => {
      const msg = err?.response?.data?.error || "Something went wrong.";
      setSnack({
        msg: msg.includes("posted") ? "You can only post once per day." : msg,
        sev: "warning",
      });
    },
  });

  /***********************  handlers  ************************************/
  const handlePitchClick = (a) => {
    if (!isCreator) return; // safeguard (should be unreachable when icon hidden)

    const id = a?.activity_id;
    if (!id) return;
    if (["Campaign", "Pitch"].includes(a.activity_type))
      navigate(`/creatorconnect/pitch/campaign/${id}`);
    else if (a.activity_type === "Partnership")
      navigate(`/creatorconnect/pitch/partnership/${id}`);
  };

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > 280 || createPost.isLoading) return;
    createPost.mutate({ text: trimmed });
  };

  /***********************  derived  *************************************/
  const feed = data?.pages.flatMap((p) => p.zapfeed) ?? [];

  /***********************  UI  ******************************************/
  return (
    <Box sx={{ maxWidth: 800, mx: "auto", px: { xs: 1, sm: 2 } }}>
      {/* composer */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.light, 0.04),
        }}
      >
        <Box display="flex" gap={2}>
          <Avatar src={photo} alt={displayName} sx={{ width: 48, height: 48 }} />
          <Box flex={1}>
            <TextField
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={2}
              maxRows={6}
              placeholder="Share something with your network…"
              fullWidth
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />

            <Box display="flex" justifyContent="space-between" mt={1}>
              <Typography
                variant="caption"
                color={text.length > 280 ? "error.main" : "text.secondary"}
              >
                {text.length}/280
              </Typography>

              <Button
                size="small"
                variant="contained"
                onClick={handleSubmit}
                disabled={!text.trim() || text.trim().length > 280 || createPost.isLoading}
              >
                Post
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* feed list */}
      <List sx={{ p: 0 }}>
        {feed.map((act) => {
          const icon = activityIcons[act.activity_type] || null;
          return (
            <Paper
              key={`${act.activity_type}-${act.activity_id ?? act.creator_id ?? act.activity_date}`}
              elevation={0}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: theme.shadows[3],
                  transform: "translateY(-2px)",
                },
              }}
            >
              <ListItem
                disableGutters
                alignItems="center"
                sx={{ pr: { xs: 10, sm: 11 } }}
                secondaryAction={
                  <Box display="flex" alignItems="center" gap={1.5}>
                    {icon && (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {icon}
                      </Box>
                    )}

                    {isCreator &&
                      ["Campaign", "Partnership", "Pitch"].includes(act.activity_type) && (
                        <IconButton
                          onClick={() => handlePitchClick(act)}
                          sx={{
                            color: theme.palette.primary.main,
                            "&:hover": { transform: "scale(1.2)" },
                          }}
                        >
                          <SendIcon />
                        </IconButton>
                      )}
                  </Box>
                }
              >
                <ListItemAvatar sx={{ minWidth: 0, mr: 2 }}>
                  <Avatar
                    src={act.pfphref || PLACEHOLDERS[1]}
                    alt={act.creator_id || "avatar"}
                    sx={{
                      width: 56,
                      height: 56,
                      border: `2px solid ${theme.palette.background.paper}`,
                      boxShadow: theme.shadows[1],
                    }}
                  />
                </ListItemAvatar>

                <ActivitySummary activity={act} />
              </ListItem>
            </Paper>
          );
        })}

        {/* skeletons */}
        {(isLoading || isFetchingNextPage) &&
          Array.from({ length: ROW_COUNT }).map((_, i) => (
            <Paper
              key={`sk-${i}`}
              sx={{ mb: 2, p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
            >
              <ListItem disableGutters alignItems="center">
                <ListItemAvatar sx={{ minWidth: 0, mr: 2 }}>
                  <Skeleton variant="circular" width={56} height={56} />
                </ListItemAvatar>
                <Box flex={1}>
                  <Skeleton variant="text" width="35%" height={24} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>
              </ListItem>
            </Paper>
          ))}

        <Box ref={sentinelRef} sx={{ height: 1 }} />

        {error && (
          <Paper
            sx={{ p: 3, textAlign: "center", bgcolor: theme.palette.error.light, borderRadius: 3 }}
          >
            <Typography color="error">Failed to load activities. Please refresh the page.</Typography>
          </Paper>
        )}
      </List>

      {/* snackbar */}
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack && (
          <Alert
            severity={snack.sev}
            variant="filled"
            onClose={() => setSnack(null)}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
